import { createSelector, createSlice, PayloadAction, prepareAutoBatched } from '@reduxjs/toolkit';
import { selectCurrentOpenedContent } from 'features/layout/features/content/contentSlice';
import { screenshareDeleted, screensharesDeleted } from 'features/layout/features/order/orderSlice';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
} from 'features/screenshare/screenshareSlice';
import { selectLocalUserId } from 'features/users/usersSlice';
import { RootState } from 'store/store';
import { ZOOMABLE_CONTENT_TYPES } from 'features/content-zoom/constants';
import { ZoomState, ZoomPositionPayload, ZoomLevelPayload } from 'features/content-zoom/types';
import {
  selectContentLibraryOpenedFile,
  fileClosed,
} from 'features/content-library/contentLibrarySlice';
import { buildZoomId } from 'features/content-zoom/utils/formatUtils';

interface ContentZoomState {
  zoom: Record<string, ZoomState>;
}

const initialState: ContentZoomState = {
  zoom: {},
};

const defaultZoomState: ZoomState = {
  scale: 1,
  offsetX: 0,
  offsetY: 0,
};

const ensureZoomEntry = (state: ContentZoomState, zoomId: string) => {
  if (!state.zoom[zoomId]) {
    state.zoom[zoomId] = { ...defaultZoomState };
  }

  return state.zoom[zoomId];
};

export const contentZoomSlice = createSlice({
  name: 'contentZoom',
  initialState,
  reducers: {
    zoomLevelChanged(state, action: PayloadAction<ZoomLevelPayload>) {
      const { zoomId, scale, resetPosition } = action.payload;
      const entry = ensureZoomEntry(state, zoomId);

      entry.scale = scale;

      if (resetPosition || scale === 1) {
        entry.offsetX = 0;
        entry.offsetY = 0;
      }
    },
    zoomPositionChanged: {
      reducer(state, action: PayloadAction<ZoomPositionPayload>) {
        const { zoomId, offsetX, offsetY } = action.payload;
        const entry = ensureZoomEntry(state, zoomId);

        entry.offsetX = offsetX;
        entry.offsetY = offsetY;
      },
      prepare: prepareAutoBatched<ZoomPositionPayload>(),
    },
    zoomReset(state, action: PayloadAction<{ zoomId: string }>) {
      const { zoomId } = action.payload;

      if (state.zoom[zoomId]) {
        state.zoom[zoomId] = { ...defaultZoomState };
      }
    },
    remoteZoomSynced(
      state,
      action: PayloadAction<{ zoomId: string; scale: number; offsetX: number; offsetY: number }>
    ) {
      const { zoomId, scale, offsetX, offsetY } = action.payload;
      const entry = ensureZoomEntry(state, zoomId);

      entry.scale = scale;
      entry.offsetX = offsetX;
      entry.offsetY = offsetY;
    },
    zoomRemoved(state, action: PayloadAction<{ zoomId: string }>) {
      delete state.zoom[action.payload.zoomId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(screenshareDeleted, (state, action) => {
      const { feedId } = action.payload;

      if (feedId) {
        delete state.zoom[feedId];
      }
    });

    builder.addCase(screensharesDeleted, (state, action) => {
      for (const feedId of action.payload.ids) {
        delete state.zoom[feedId];
      }
    });

    builder.addCase(fileClosed, (state, action) => {
      for (const key of Object.keys(state.zoom)) {
        if (key.startsWith(action.payload)) {
          delete state.zoom[key];
        }
      }
    });
  },
});

export const { zoomLevelChanged, zoomPositionChanged, remoteZoomSynced, zoomReset, zoomRemoved } =
  contentZoomSlice.actions;

export const selectZoomState = (state: RootState, zoomId: string) => state.contentZoom.zoom[zoomId];

export const selectIsZoomed = (state: RootState, zoomId: string) =>
  (state.contentZoom.zoom[zoomId]?.scale || 1) > 1;

export const selectActiveZoomId = createSelector(
  [
    selectCurrentOpenedContent,
    selectShowAllScreens,
    selectSelectedScreenshare,
    selectLocalUserId,
    selectContentLibraryOpenedFile,
  ],
  (content, showAllScreens, selectedScreenshare, localUserId, openedFile) => {
    if (!content) {
      return null;
    }

    if (
      content.type === 'screenshare' &&
      selectedScreenshare?.userId !== localUserId &&
      !showAllScreens
    ) {
      return content.id;
    }

    if (content.type === 'file' && openedFile && ZOOMABLE_CONTENT_TYPES.includes(openedFile.type)) {
      return buildZoomId(openedFile.id, openedFile.page);
    }

    return null;
  }
);

export const selectIsActiveContentZoomed = createSelector(
  [selectActiveZoomId, (state: RootState) => state.contentZoom.zoom],
  (zoomId, zoom) => !!zoomId && (zoom[zoomId]?.scale || 1) > 1
);

export const selectCurrentActiveZoomState = createSelector(
  [selectActiveZoomId, (state: RootState) => state.contentZoom.zoom],
  (zoomId, zoom) => {
    if (!zoomId) {
      return null;
    }

    const zoomState = zoom[zoomId];

    if (!zoomState) {
      return null;
    }

    return zoomState;
  }
);

export const selectIsContentZoomable = createSelector(
  [selectContentLibraryOpenedFile],
  (openedFile) => !!openedFile && ZOOMABLE_CONTENT_TYPES.includes(openedFile.type)
);

export default contentZoomSlice.reducer;
